import parse from 'url-parse';
import qs from 'query-string';
import { sha256 } from 'js-sha256';

const genericTotalMapping = {
    adjustedorderitemprice_daily_eur: 'basePriceEURDAILY',
    adjustedorderitemprice_daily_gbp: 'basePriceGBPDAILY',
    adjustedorderitemprice_annualised_eur: 'basePriceEURFIXED',
    adjustedorderitemprice_annualised_gbp: 'basePriceGBPFIXED',
};

/**
 * @param {string} event
 * @param {string} effect
 */
export const getEventAndEffect = (event, effect) => ({ event, effect });

/**
 * @param {string} value
 */
export const getSameEventAndEffect = (value) => getEventAndEffect(value, value);

export const getPageAnalytics = ({
    region,
    currency = '',
    business,
    pageName,
    pageType,
    featuresList = [],
    primaryCategory,
    subCategory1,
    subCategory2,
    extraAttributes,
    extraPageProps,
    ...rest
}) => ({
    event: 'trackPage',
    page: {
        pageInfo: getPageInfo({ pageName, location: window.location, ...rest }),
        category: {
            pageType: pageType || pageName,
            primaryCategory,
            subCategory1,
            subCategory2,
        },
        attributes: {
            businessName: business,
            region,
            currency: currency.toLowerCase(),
            featuresList,
            ...(typeof extraAttributes === 'object' && extraAttributes),
        },
        ...(typeof extraPageProps === 'object' && extraPageProps),
    },
});

export const removeQueryParams = (urlString, fields = ['email'], isRemoveLocale = false) => {
    const parsed = parse(urlString, true);
    fields.forEach((field) => {
        delete parsed.query[field];
    });

    const queryParams = Object.keys(parsed.query).length > 0 ? `?${qs.stringify(parsed.query)}` : '';
    // url-parse adds localhost as the hostname to some relative urls so we address that here
    const isNotRelative = parsed.hostname !== 'localhost' && parsed.hostname !== '';

    let processedUrl = `${parsed.pathname}${queryParams}`;

    // if locale exists in string, strip locale from url
    if (isRemoveLocale) {
        const regex = /\/\w{2}-\w{2}\//i;
        const matches = regex.exec(parsed.pathname);
        if (matches) {
            return processedUrl.substring(matches.index + 6);
        }
    }

    return isNotRelative ? parsed.toString() : processedUrl;
};

export const getPageInfo = ({
    location = {},
    country,
    language,
    variant = 'default',
    deviceType,
    pageName = '',
    prevUrl = '',
    useIdentifier = false,
    gender,
}) => {
    const { pathname = '', search = '' } = location;
    const destUrl = removeQueryParams(`${pathname}${search}`);
    const referringURL = removeQueryParams(prevUrl || (typeof document !== 'undefined' && document.referrer));
    const shortURL = removeQueryParams(`${pathname}${search}`, undefined, true);
    return {
        pageName: useIdentifier ? pageName : pageName?.toLowerCase(),
        environment: (typeof document !== 'undefined' && document.querySelector('body').dataset.analytics) || 'prod',
        sysEnv: getEnvironment(deviceType),
        geoRegion: country?.toLowerCase(),
        language: language?.toLowerCase(),
        destinationURL: destUrl,
        shortURL,
        referringURL,
        variant: variant?.toLowerCase(),
        ...(gender ? { gender } : {}),
    };
};

export const getForexAnalytics = (genericTotal, mapping) => {
    const currencyDetails = genericTotal?.reduce((accumulator, current) => {
        const key = mapping?.[current.name?.trim().toLowerCase()];
        return !key ? accumulator : { ...accumulator, [key]: calculatePrice(current) };
    }, {});
    return currencyDetails || {};
};

export const getCartAnalytics = ({
    lineItems,
    totals = {},
    orderId,
    genericTotal,
    basePrice,
    basePriceGBP,
    itemSubtotal,
    transactionTotal,
    saleOrderFlag,
    taxAndDuty,
    localCustomsFee,
    storeCredit,
    taxAndDutyFlag,
    useIdentifier = false,
}) => {
    // data is mapped from cart-state selector
    const { currency = '', rawPriceWithTax, priceWithShippingTax, rawShippingPrice, itemSubtotalExTaxDuty } = totals;
    const genericTotalMapping = {
        totalproductprice_daily_eur: 'basePriceEURDAILY',
        totalproductprice_daily_gbp: 'basePriceGBPDAILY',
        totalproductpricewithpromotion_annualised_eur: 'basePriceEURFIXED',
        totalproductpricewithpromotion_annualised_gbp: 'basePriceGBPFIXED',
        totalshippingchargewithadjustment_daily_gbp: 'shippingGBP',
    };

    const priceWithShippingTaxGBP = getPriceWithShippingTaxGBP(genericTotal);

    const sustainabilityFlag = lineItems?.map((item) => item.consideredBadge?.identifier === 'CONSIDERED').includes(true);

    return {
        cartID: orderId,
        total: {
            currency: currency?.toLowerCase(),
            basePrice,
            priceWithTax: rawPriceWithTax,
            priceWithShippingTax,
            priceWithShippingTaxGBP,
            shipping: rawShippingPrice,
            basePriceGBP,
            itemSubtotal,
            transactionTotal,
            taxAndDuty,
            itemSubtotalExTaxDuty,
            localCustomsFee,
            storeCredit,
            ...getForexAnalytics(genericTotal, genericTotalMapping),
        },
        attributes: {
            quantity: lineItems?.length || 0,
            taxAndDutyFlag,
            saleOrderFlag,
            sustainabilityFlag,
        },
        item: getItemsAnalytics(lineItems, taxAndDutyFlag, useIdentifier),
    };
};

/**
 * Does all item data mapping
 * @param {Array} items
 */
export const getItemsAnalytics = (items = [], taxAndDutyFlag, useIdentifier = false) =>
    items.map(
        ({
            partNumber,
            colourPartNumber,
            name,
            nameEN,
            productId,
            brand,
            brandId,
            quantity,
            size,
            category,
            price,
            genericTotal,
            onSale,
            onPromo,
            badges = [],
            isJIT,
            analyticsAttributes,
            consideredBadge,
        }) => ({
            productInfo: {
                productID: colourPartNumber || productId,
                name: analyticsAttributes?.name || analyticsAttributes?.nameEN || nameEN || name,
                sku: partNumber,
                manufacturer: brand,
                manufacturer_id: brandId,
                quantity: quantity,
                size: size,
                ...(analyticsAttributes?.gender ? { gender: analyticsAttributes?.gender } : {}),
            },
            category: {
                primaryCategory: analyticsAttributes?.category?.primaryCategory || category.primaryCategory,
                subCategory1: analyticsAttributes?.category?.subCategory1 || category.subCategory1,
                subCategory2: analyticsAttributes?.category?.subCategory2 || category.subCategory2,
                ids: analyticsAttributes?.category?.ids || category.ids,
            },
            price: price
                ? {
                      currency: price.currency?.toLowerCase(),
                      basePrice: price.xitem_baseOrderItemPrice || price.rawUnitPrice,
                      priceWithTax: price.rawFinalPrice,
                      displayPrice: price.rawFinalPrice,
                      basePriceGBP: price.xitem_baseOrderItemPrice_Annualised_GBP || price.xitem_baseOrderItemPrice || price.rawUnitPrice,
                      fullPrice: price.fullPrice,
                      taxAndDuty: price.taxAndDuty,
                      displayPriceExTaxDuty: price.displayPriceExTaxDuty,
                      discountAmount: price.discountAmount,
                      bestPrice: price.minPrice,
                      ...getForexAnalytics(genericTotal, genericTotalMapping),
                  }
                : undefined,
            quantity: quantity,
            attributes: {
                categoryIdList: category?.ids,
                onSale: onSale,
                unitDuties: price?.rawShippingTax,
                stock: toAnalyticsStockValue(badges),
                justInTimeFlag: !!isJIT,
                promoDescription: Array.isArray(price?.promotions) ? price.promotions.map(({ description }) => description) : [],
                taxAndDutyFlag,
                saleFlag: getSaleFlag(onSale, onPromo),
                badges: useIdentifier ? getTranslatedBadges(badges) : badges,
                sustainabilityFlag: !!consideredBadge,
            },
        }),
    );

export function getSaleFlag(sale, promo) {
    let saleFlag;
    if (!sale && !promo) {
        saleFlag = 'full price';
    } else if (sale && !promo) {
        saleFlag = 'on sale';
    } else if (promo && !sale) {
        saleFlag = 'promo';
    } else {
        saleFlag = 'sale and promo';
    }
    return saleFlag;
}

export const getEnvironment = (environment) => {
    switch (environment) {
        case 'ios':
        case 'android':
            return `${environment} app web view`;
        default:
            return 'website';
    }
};

export const toAnalyticsStockValue = (badges) => {
    const stockMappings = {
        BADGE_SOLD_OUT: 'out_of_stock',
        BADGE_LOW_STOCK: 'low_stock',
        BADGE_ONLY_ONE_LEFT: 'one_left',
    };
    const wcsBadge = badges?.find(({ type }) => type === 'STOCK')?.key;

    return stockMappings[wcsBadge] || 'in_stock';
};

export const calculatePrice = (price) => {
    return price.value?.amount > 0 ? price.value.amount / price.value.divisor : 0;
};

export const getPriceWithShippingTaxGBP = (genericTotal) => {
    const genericTotalGBP = genericTotal?.find(({ name }) => name.trim().toLowerCase() === 'GrandTotal_Daily_GBP'.trim().toLowerCase());
    if (genericTotalGBP) {
        return calculatePrice(genericTotalGBP);
    }
};

export const getTranslatedBadges = (badges = []) =>
    badges.map((badge) => {
        if (badge?.key) {
            let translatedBadge = { ...badge };
            translatedBadge.label = getBadgeValueFromIdentifier(badge.key);
            return translatedBadge;
        }
        return badge;
    });

/**
 * Used for transforming badge identifiers into readable text for analytics
 *
 * @param {string} identifier - Identifier to transform
 * @return {string} Transformed identifier
 */
export const getBadgeValueFromIdentifier = (identifier = '') => identifier.split('_').splice(1).join(' ').toLowerCase();

export const digestUser = (email) => (user) => {
    if (user?.attributes?.logged === 'anonymous' && typeof email === 'string') {
        return {
            attributes: { logged: 'anonymous' },
            profile: {
                profileInfo: {
                    emailSHA256: sha256(email?.toLowerCase()?.trim()),
                },
            },
        };
    } else {
        return user;
    }
};

export function getAnalyticsPaymentType(paymentId = '') {
    // format paymentId string to how analytics want it, e.g.
    // CREDIT_CARD/23423424234 => creditcard
    // PAYPAL => paypal
    // APPLEPAY => apple pay
    const result = paymentId.split('/')[0].split('_').join('').toLowerCase();
    return result.replace('applepay', 'apple pay');
}

export const getTransactionAnalytics = (props = {}) => {
    const {
        userStatus,
        orderRef,
        taxAndDutyFlag,
        saleOrderFlag,
        paymentOption = [],
        cardTypeInfo,
        totals,
        shippingOption,
        shippingAddress,
        useIdentifier = false,
    } = props;

    const statusTypes = {
        R: 'existing',
        G: 'guest',
        N: 'new',
    };

    const cardType = cardTypeInfo?.cardType;
    const paymentTypeList = !!paymentOption.length && paymentOption.map((option) => getAnalyticsPaymentType(option));
    const voucherCodeList = !!totals?.promotions?.length && totals.promotions.map(({ code }) => code);
    const attributes = {
        orderType: statusTypes[userStatus],
        saleOrderFlag,
        taxAndDutyFlag,
        ...(cardType ? { cardType } : {}),
        ...(paymentTypeList && { paymentTypeList }),
        ...(paymentTypeList && { paymentType: paymentTypeList[0] }),
        ...(voucherCodeList && { voucherCodeList }),
    };

    const cart = getCartAnalytics({ ...props, saleOrderFlag, useIdentifier });
    const { total, ...rest } = cart;

    return {
        transactionID: orderRef,
        ...rest,
        attributes,
        total: {
            ...(shippingOption && { shippingMethod: shippingOption.nameEN }),
            ...(shippingAddress && { shippingCountry: shippingAddress.country }),
            ...total,
        },
    };
};
